// src/components/tableros/Campo/Tablas/VerticalEventTable.js
import React, { useEffect, useRef } from 'react';

const VerticalEventTable = ({
  // Ahora recibimos un objeto eventData con { eventName, eventSubtitle, participants }
  eventData = {
    eventName: 'No hay eventos seleccionados',
    eventSubtitle: '',
    participants: [
      {
        position: '1',
        name: 'Atleta',
        team: 'Equipo',
        height: '0',
        attempts: [],
        lane: ''
      }
    ]
  },

  displayRows,
  tableWidth = 1000,
  tableHeight = 800,

  // Colores
  tableBackgroundColor = '#1a1a1a',
  headerColor = '#2c0369',
  positionColor = '#4c516d',
  positionBackgroundColor = '#4c516d',
  nameBackgroundColor = '#515e7b',
  heightBackgroundColor = '#000000',
  headerFontColor = '#ffffff',
  athleteNameColor = '#ffffff',
  teamNameColor = '#e0e0e0',
  heightColor = '#ffffff',
  laneColor = '#ffffff',
  columnsHeaderColor = '#4c516d',

  // Colores para intentos
  failColor = '#b71c1c',
  passColor = '#0d47a1',
  successColor = '#1b5e20',
  tableRowOddColor = '#252525',
  tableRowEvenColor = '#1e1e1e',

  // Tipografía
  headerFontSize = 24,
  positionFontSize = 48,
  nameFontSize = 36,
  teamFontSize = 24,
  heightFontSize = 54,
  attemptFontSize = 32,

  // Familias de fuentes
  headerFontFamily = 'Roboto',
  nameFontFamily = 'Roboto',
  numbersFontFamily = 'Roboto Mono',

  // Opciones de visualización
  showLane = true,
}) => {
  const listContainerRef = useRef(null);

  // Formatea la altura
  const formatHeight = height => {
    const meters = typeof height === 'string' ? parseFloat(height) : height;
    if (isNaN(meters)) return height;
    return `${meters.toFixed(2)} m`;
  };

  // Prepara el array de atletas a mostrar
  const athletes = displayRows === 1
    ? [eventData.participants[0]]
    : eventData.participants.slice(0, displayRows);

  // Reset de scroll al cambiar filas
  useEffect(() => {
    if (listContainerRef.current) {
      listContainerRef.current.scrollTop = 0;
    }
  }, [displayRows]);

  const hasValidResults = () =>
    athletes.some(a => a.position != null && a.position !== '');

  const shouldDisplayLaneColumn = () => {
    if (displayRows === 1) return showLane;
    const has = hasValidResults();
    return !has || (has && showLane);
  };

  const calculateRowHeight = () => {
    const available = tableHeight - 150;
    return available / displayRows;
  };

  const renderLargeAttempt = (attempt, i, total) => {
    let bg = 'transparent', color = 'transparent';
    if (attempt === 'X') bg = failColor;
    if (attempt === 'P') bg = passColor;
    if (attempt === 'O') bg = successColor;

    return (
      <div key={i} style={{
        backgroundColor: bg,
        width: `${100/total}%`,
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          color: '#ffffff',
          fontSize: `${attemptFontSize}px`,
          fontWeight: 'bold',
          fontFamily: numbersFontFamily
        }}>{attempt}</div>
      </div>
    );
  };

  const renderSmallAttempt = (attempt, i) => {
    let bg = 'transparent', color = 'transparent';
    if (attempt === 'X') bg = failColor;
    if (attempt === 'P') bg = passColor;
    if (attempt === 'O') bg = successColor;

    return (
      <div key={i} style={{
        backgroundColor: bg,
        width: '30px',
        height: '30px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '0 2px',
        borderRadius: '3px',
        color: '#ffffff',
        fontWeight: 'bold',
        fontSize: `${Math.min(attemptFontSize*0.5, 18)}px`,
        fontFamily: numbersFontFamily
      }}>{attempt}</div>
    );
  };

  // Vista individual
  if (displayRows === 1) {
    const athlete = athletes[0] || {};
    const rawH = typeof athlete.height === 'string'
      ? parseFloat(athlete.height)
      : athlete.height;
    const formattedHeight = formatHeight(rawH);
    const hasAt = athlete.attempts?.length > 0;
    const countAt = athlete.attempts?.length || 0;
    const headerH = '10%', footerH = '5%', contentH = '85%';
    const topH = '50%', bottomH = '50%';
    const posW = '20%', nameW = '80%';
    const heightW = hasAt ? '40%' : '100%';
    const atW = hasAt ? '60%' : '0%';

    return (
      <div style={{
        position: 'relative',
        width: `${tableWidth}px`,
        height: `${tableHeight}px`,
        backgroundColor: tableBackgroundColor,
        color: headerFontColor,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          width:'100%', height: headerH,
          backgroundColor: headerColor,
          padding:'10px',
          display:'flex', justifyContent:'center', alignItems:'center'
        }}>
          <div style={{ textAlign:'center' }}>
            <div style={{
              fontWeight:'bold',
              fontSize:`${headerFontSize}px`,
              color: headerFontColor,
              fontFamily: headerFontFamily
            }}>{eventData.eventName}</div>
            {eventData.eventSubtitle && (
              <div style={{
                fontSize:`${headerFontSize*0.6}px`,
                color: headerFontColor,
                opacity:0.8,
                marginTop:'5px',
                fontFamily: headerFontFamily
              }}>{eventData.eventSubtitle}</div>
            )}
          </div>
        </div>
        {/* Contenido */}
        <div style={{
          flex:1, height:contentH,
          display:'flex', flexDirection:'column'
        }}>
          <div style={{ display:'flex', height:topH }}>
            <div style={{
              width:posW,
              backgroundColor: positionBackgroundColor,
              display:'flex', justifyContent:'center', alignItems:'center'
            }}>
              <div style={{
                fontSize:`${positionFontSize}px`,
                fontWeight:'bold',
                color: headerFontColor,
                fontFamily: numbersFontFamily
              }}>{athlete.position}</div>
            </div>
            <div style={{
              width:nameW,
              backgroundColor:nameBackgroundColor,
              padding:'16px',
              display:'flex', flexDirection:'column', justifyContent:'center'
            }}>
              <div style={{
                fontSize:`${nameFontSize}px`,
                fontWeight:'bold',
                color: athleteNameColor,
                fontFamily: nameFontFamily
              }}>{athlete.name}</div>
              <div style={{
                fontSize:`${teamFontSize}px`,
                color: teamNameColor,
                fontFamily: nameFontFamily,
                marginTop:'8px'
              }}>{athlete.team}</div>
            </div>
          </div>
          <div style={{ display:'flex', height:bottomH }}>
            <div style={{
              width: heightW,
              backgroundColor: heightBackgroundColor,
              display:'flex', justifyContent:'center', alignItems:'center'
            }}>
              <div style={{
                fontSize:`${heightFontSize}px`,
                fontWeight:'bold',
                color: heightColor,
                fontFamily: numbersFontFamily
              }}>{formattedHeight}</div>
            </div>
            {hasAt && (
              <div style={{ width:atW, display:'flex', height:'100%' }}>
                {athlete.attempts.map((a,i) =>
                  renderLargeAttempt(a, i, countAt)
                )}
              </div>
            )}
          </div>
        </div>
        {/* Footer */}
        <div style={{
          width:'100%', height:footerH,
          padding:'5px',
          backgroundColor: tableBackgroundColor,
          textAlign:'right'
        }}>
          <div style={{
            color: headerFontColor,
            fontSize:'10px',
            opacity:0.6,
            fontFamily: headerFontFamily
          }}>Athletic LIVE</div>
        </div>
      </div>
    );
  }

  // Vista múltiple
  const displayLane = shouldDisplayLaneColumn();

  return (
    <div style={{
      width: `${tableWidth}px`,
      height: `${tableHeight}px`,
      backgroundColor: tableBackgroundColor,
      color: headerFontColor,
      display:'flex', flexDirection:'column', overflow:'hidden'
    }}>
      {/* Header */}
      <div style={{
        backgroundColor: headerColor,
        padding:'10px',
        textAlign:'center'
      }}>
        <div style={{
          fontSize:`${headerFontSize}px`,
          fontWeight:'bold',
          color: headerFontColor,
          fontFamily: headerFontFamily
        }}>{eventData.eventName}</div>
        {eventData.eventSubtitle && (
          <div style={{
            fontSize:`${headerFontSize*0.6}px`,
            color: headerFontColor,
            opacity:0.8,
            marginTop:'5px',
            fontFamily: headerFontFamily
          }}>{eventData.eventSubtitle}</div>
        )}
      </div>
      {/* Columnas */}
      <div style={{
        display:'flex',
        backgroundColor: columnsHeaderColor,
        padding:'8px 16px',
        fontWeight:'bold',
        color: headerFontColor,
        fontSize:`${headerFontSize*0.7}px`,
        fontFamily: headerFontFamily
      }}>
        {hasValidResults() && <div style={{ width:'8%', textAlign:'center' }}>Pos</div>}
        {displayLane && <div style={{ width:'8%', textAlign:'center' }}>Línea</div>}
        <div style={{ flex:1, paddingLeft:'10px' }}>Atleta</div>
        <div style={{ width:'25%', textAlign:'center' }}>Altura</div>
        <div style={{ width:'25%', textAlign:'center' }}>Intentos</div>
      </div>
      {/* Lista */}
      <div ref={listContainerRef} style={{ flex:1, overflow:'auto' }}>
        {athletes.map((ath, idx) => {
          const formattedH = formatHeight(ath.height);
          const rowColor = idx%2===0 ? tableRowOddColor : tableRowEvenColor;
          const rowH = calculateRowHeight();
          const hasAt2 = ath.attempts?.length > 0;

          return (
            <div key={idx} style={{
              display:'flex',
              backgroundColor: rowColor,
              padding:'8px 16px',
              borderBottom:'1px solid rgba(255,255,255,0.1)',
              alignItems:'center',
              minHeight:`${rowH}px`,
              transition:'background-color 0.2s'
            }}
            onMouseEnter={e=>e.currentTarget.style.backgroundColor='rgba(255,255,255,0.08)'}
            onMouseLeave={e=>e.currentTarget.style.backgroundColor=rowColor}
            >
              {hasValidResults() && (
                <div style={{ width:'8%', textAlign:'center' }}>
                  <div style={{
                    color: positionColor,
                    fontWeight:'bold',
                    fontSize:`${positionFontSize*0.5}px`,
                    fontFamily: numbersFontFamily
                  }}>{ath.position}</div>
                </div>
              )}
              {displayLane && (
                <div style={{
                  width:'8%',
                  textAlign:'center',
                  color: laneColor,
                  fontSize:`${positionFontSize*0.5}px`,
                  fontWeight:'bold',
                  fontFamily: numbersFontFamily
                }}>{ath.lane}</div>
              )}
              <div style={{ flex:1, paddingLeft:'10px' }}>
                <div style={{
                  color: athleteNameColor,
                  fontSize:`${nameFontSize*0.5}px`,
                  fontWeight:'bold',
                  fontFamily: nameFontFamily
                }}>{ath.name}</div>
                <div style={{
                  color: teamNameColor,
                  fontSize:`${teamFontSize*0.5}px`,
                  fontFamily: nameFontFamily,
                  marginTop:'4px'
                }}>{ath.team}</div>
              </div>
              <div style={{
                width:'25%',
                textAlign:'center',
                display:'flex',
                justifyContent:'center',
                alignItems:'center',
                padding:'0 10px'
              }}>
                <div style={{
                  fontSize:`${heightFontSize*0.4}px`,
                  fontWeight:'bold',
                  color: heightColor,
                  fontFamily: numbersFontFamily
                }}>{formattedH}</div>
              </div>
              <div style={{
                width:'25%',
                display:'flex',
                justifyContent:'center',
                alignItems:'center',
                gap:'4px',
                padding:'8px'
              }}>
                {hasAt2
                  ? ath.attempts.map((a,i)=>renderSmallAttempt(a,i))
                  : <div style={{
                      color:'rgba(255,255,255,0.5)',
                      fontSize:'0.8rem',
                      fontFamily:nameFontFamily
                    }}>Sin intentos</div>
                }
              </div>
            </div>
          );
        })}
      </div>
      {/* Footer */}
      <div style={{
        padding:'5px',
        textAlign:'right',
        fontSize:'10px',
        color: headerFontColor,
        opacity:0.6,
        fontFamily: headerFontFamily
      }}>Athletic LIVE</div>
    </div>
  );
};

export default VerticalEventTable;
